import { Checkbox, UIText2 } from '@cian/ui-kit';
import { IconActionChevronDownSmall16 } from '@cian/ui-kit-design-tokens/icons';
import cn from 'clsx';
import { ChangeEvent, useCallback, useState } from 'react';

import styles from './ExpandableCheckboxGrid.css';

export interface IExpandableCheckboxGridOption<T> {
  label: string;
  value: T;
}

interface IProps<T> {
  options: IExpandableCheckboxGridOption<T>[];
  value: T[];
  onChange: (next: T[]) => void;
  expandLabel?: string;
  collapseLabel?: string;
  /** Если true — список всегда полностью раскрыт, кнопка «Показать всё»/«Свернуть» не рендерится. */
  alwaysExpanded?: boolean;
}

export function ExpandableCheckboxGrid<T extends string | number>({
  options,
  value,
  onChange,
  expandLabel = 'Показать всё',
  collapseLabel = 'Свернуть',
  alwaysExpanded = false,
}: IProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const isOpen = alwaysExpanded || expanded;

  const handleToggle = useCallback(() => setExpanded(prev => !prev), []);

  const handleItemChange = useCallback(
    (option: T) => (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const next = checked ? [...value, option] : value.filter(v => v !== option);
      onChange(next);
    },
    [onChange, value],
  );

  return (
    <div className={styles['container']}>
      <div className={cn(styles['options'], { [styles['open']]: isOpen })}>
        {options.map(option => (
          <Checkbox
            key={String(option.value)}
            checked={value.includes(option.value)}
            label={option.label}
            onChange={handleItemChange(option.value)}
          />
        ))}
      </div>
      {!alwaysExpanded && (
        <div className={styles['expand-button']} onClick={handleToggle}>
          <UIText2 color="primary_100">{expanded ? collapseLabel : expandLabel}</UIText2>
          <div className={cn(styles['arrow'], { [styles['rotated']]: expanded })}>
            <IconActionChevronDownSmall16 color="icon-main-default" />
          </div>
        </div>
      )}
    </div>
  );
}
